/**
 * Table with a GFM serializer that escapes `|` inside cells (also inside code
 * spans, which GFM requires) and falls back to raw <table> HTML for tables
 * markdown cannot express (merged cells, multi-paragraph cells, header-less).
 */
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { DOMSerializer } from '@tiptap/pm/model';

const hasSpan = (n) => n.attrs.colspan > 1 || n.attrs.rowspan > 1;
const kids = (node) => {
  const out = [];
  node.forEach((c) => out.push(c));
  return out;
};

function isMarkdownSerializable(node) {
  const rows = kids(node);
  const firstRow = rows[0];
  const bodyRows = rows.slice(1);
  if (kids(firstRow).some((c) => c.type.name !== 'tableHeader' || hasSpan(c) || c.childCount > 1)) return false;
  if (bodyRows.some((r) => kids(r).some((c) => c.type.name === 'tableHeader' || hasSpan(c) || c.childCount > 1))) return false;
  return true;
}

export const TableMd = Table.extend({
  addStorage() {
    return {
      markdown: {
        serialize(state, node) {
          if (!isMarkdownSerializable(node)) {
            const dom = DOMSerializer.fromSchema(node.type.schema).serializeNode(node);
            const wrap = document.createElement('div');
            wrap.appendChild(dom);
            state.write(wrap.innerHTML);
            state.closeBlock(node);
            return;
          }
          const origText = state.text.bind(state);
          state.text = (t, esc) => origText(String(t).replace(/(?<!\\)\|/g, '\\|'), esc);
          state.inTable = true;
          try {
            node.forEach((row, p, i) => {
              state.write('| ');
              row.forEach((col, p2, j) => {
                if (j) state.write(' | ');
                const cellContent = col.firstChild;
                if (cellContent.textContent.trim()) state.renderInline(cellContent);
              });
              state.write(' |');
              state.ensureNewLine();
              if (!i) {
                const delimiterRow = Array.from({ length: row.childCount }).map(() => '---').join(' | ');
                state.write(`| ${delimiterRow} |`);
                state.ensureNewLine();
              }
            });
          } finally {
            state.text = origText;
            state.inTable = false;
          }
          state.closeBlock(node);
        },
        parse: {},
      },
    };
  },
});

// GFM cells hold inline content only: keep cells to a single paragraph so block
// components (columns, callouts, steps...) can never be nested inside a table
// (which markdown cannot express and would force an HTML fallback).
export const TableCellMd = TableCell.extend({ content: 'paragraph' });
export const TableHeaderMd = TableHeader.extend({ content: 'paragraph' });
